import { faBuilding as fasBuilding } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import { Button, FormCurrency, FormDecidable } from "bm-ui-kit";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import {
	MAX_LOAN_AMOUNT,
	MAX_MONTHLY_INCOME,
	MAX_REAL_ESTATE_VALUE,
	MAX_REAL_ESTATE_VALUE_IN_CASE_OF_HOUSE,
	MIN_LOAN_AMOUNT,
	MIN_MONTHLY_INCOME,
	MIN_REAL_ESTATE_VALUE,
} from "../data";
import type { FormFields, Step1StepFields } from "../types";

export class Step1 extends AbstractStep<Step1StepFields> {
	protected initValues(): Partial<Step1StepFields> {
		return {
			realEstateValue: undefined,
			loanAmount: undefined,
			monthlyIncome: undefined,
			has2YearsLegalHealthInsurance: undefined,
			wantsFirstHouse: undefined,
			typeOfProperty: undefined,
		};
	}

	protected renderContent(_values: Step1StepFields, _form: FormApi<Step1StepFields>): ReactNode {
		const disabled = this.isDisabled();

		return (
			<>
				<Card.Header className="bg-secondary-gradient text-white text-center">
					<div className="h2">
						<strong>
							Tudd meg, hogy mennyivel jársz jobban az új, 3%-os első lakásvásárlási hitellel!
						</strong>
					</div>
				</Card.Header>
				<Card.Body className="d-flex flex-column">
					<FormDecidable
						label="Milyen ingatlant vásárolnál?"
						name="typeOfProperty"
						disabled={disabled}
						trueTitle="Lakás"
						falseTitle="Lakóház"
						className="my-0"
						required
					/>
					<FormCurrency
						label="Ingatlan vételára"
						name="realEstateValue"
						validate={this.validRealEstateValue}
						prepend={<FontAwesomeIcon icon={fasBuilding} />}
						disabled={disabled}
						required
					/>
					<FormCurrency
						label="Hitelösszeg (max. 50 000 000 Ft)"
						name="loanAmount"
						validate={this.validLoanValue}
						disabled={disabled}
						required
					/>
					<FormCurrency
						label="Hiteligénylő(k) jövedelme"
						name="monthlyIncome"
						validate={this.validMonthlyIncome}
						disabled={disabled}
						required
					/>
					<FormDecidable
						label="Első ingatlanod vásárolnád?"
						name="wantsFirstHouse"
						disabled={disabled}
						className="my-0"
						required
						infoTooltip="Nem lehet/lehetett 10 éven belül 50%-ot meghaladó tulajdoni hányadod. Kivéve, ha az ingatlant elbontották, értéke nem haladja meg 15 millió Ft-ot, haszonélvezettel terhelt és a haszonélvező bent lakik."
					/>
					<FormDecidable
						label="Van legalább 2 éves TB jogviszonyod?"
						name="has2YearsLegalHealthInsurance"
						disabled={disabled}
						className="my-0"
						infoTooltip="Az utolsó 180 napnak Magyarországon folytatott munkaviszonyból, vállalkozói jogviszonyból kell származnia."
						required
					/>
				</Card.Body>
				<Card.Footer className="d-flex">
					<Button type="submit" className="font-weight-bold" block>
						Tovább
					</Button>
				</Card.Footer>
			</>
		);
	}

	private validLoanValue(value: number, values: FormFields): string {
		const { realEstateValue } = values;

		if (value < MIN_LOAN_AMOUNT) {
			return `Nem lehet kisebb a hitelösszeg mint ${formatText(MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (value > MAX_LOAN_AMOUNT) {
			return `A maximálisan felvehető hitelösszeg ${formatText(MAX_LOAN_AMOUNT, "ft")}!`;
		}

		if (value > realEstateValue * 0.9) {
			return `A hitelösszeg nem lehet nagyobb az ingatlan értékének 90%-ánál (${formatText(realEstateValue * 0.9, "ft")})!`;
		}
	}

	private validMonthlyIncome(value: number): string {
		if (value < MIN_MONTHLY_INCOME) {
			return `
			A megadható minimális összjövedelem ${formatText(MIN_MONTHLY_INCOME, "ft")}, amely megegyezik az aktuális minimálbérrel!`;
		}

		if (value > MAX_MONTHLY_INCOME) {
			return `A jövedelem nem lehet több mint ${formatText(MAX_MONTHLY_INCOME, "ft")}!`;
		}
	}

	private validRealEstateValue(value: number, values: FormFields): string {
		const maxRealEstateValue = values.typeOfProperty
			? MAX_REAL_ESTATE_VALUE
			: MAX_REAL_ESTATE_VALUE_IN_CASE_OF_HOUSE;

		const propertyTypeString = values.typeOfProperty ? "lakás" : "lakóház";

		if (value < MIN_REAL_ESTATE_VALUE) {
			return `Nem lehet az ingatlan értéke ${formatText(MIN_REAL_ESTATE_VALUE, "ft")}!`;
		}

		if (value > maxRealEstateValue) {
			return `A vásárolni kívánt ingatlan maximális értéke ${propertyTypeString} esetén ${formatText(maxRealEstateValue, "ft")}!`;
		}
	}

	protected getComponentName(): string {
		return "Step1";
	}
}
